/**
 * Search Suggest API Module
 * Handles fetching search suggestions from SearchSuggests.json
 */

import { ApiBase } from "../../core/api-base";
import { z } from "zod";
import { GetSearchSuggestions200Response, SaveSearchHistoryBody } from "../../api/generated/search.zod";

export type SearchSuggestValue = unknown;

type SearchSuggestionsResponse = z.infer<typeof GetSearchSuggestions200Response>;
type SearchSuggestionsData = NonNullable<SearchSuggestionsResponse["data"]>;
type SearchSuggestionsApiItem = NonNullable<SearchSuggestionsData["suggestions"]>[number];
type SaveSearchHistoryPayload = z.infer<typeof SaveSearchHistoryBody>;
type SearchTypeValue = NonNullable<SaveSearchHistoryPayload["search_type"]>;
type SectionIdValue = NonNullable<SaveSearchHistoryPayload["section_id"]>;

export type SearchSuggestItemMetadata = NonNullable<SearchSuggestionsApiItem["metadata"]> & {
    [key: string]: SearchSuggestValue;
};

export type SearchSuggestItem = SearchSuggestionsApiItem & {
    metadata?: SearchSuggestItemMetadata;
    search_type?: SearchSuggestionsApiItem["type"];
    total_score?: number;
    [key: string]: SearchSuggestValue;
};

export type SearchSuggestSearchOptions = {
    searchType?: string;
    sectionId?: string;
    /**
     * Exact-match board scoping for post_title suggestions (metadata.board_id).
     * sectionId alone can't distinguish between boards — mapBoardIdToSectionId()
     * buckets most boards (e.g. beauty_news, editorial_reviews) into the same
     * "general" section, so a board search on one would leak in matches from
     * the other. boardId scopes precisely to the current page's own board.
     */
    boardId?: string;
    /** Types to drop from results even though searchType leaves them broadly unrestricted. */
    excludeTypes?: string[];
    limit?: number;
};

function toSearchTypeValue(value: string): SearchTypeValue | undefined {
    const normalized = String(value || "").trim();
    switch (normalized) {
        case "category":
        case "brand":
        case "product":
        case "keyword":
        case "post_title":
        case "user":
        case "hashtag":
            return normalized;
        default:
            return undefined;
    }
}

function toSectionIdValue(value: string): SectionIdValue | undefined {
    const normalized = String(value || "").trim();
    switch (normalized) {
        case "tech":
        case "programming":
        case "products":
        case "frontend":
        case "devops":
        case "general":
            return normalized;
        default:
            return undefined;
    }
}

class SearchSuggestApi extends ApiBase {
    public suggestsCache: SearchSuggestItem[] | null;

    public cacheTimestamp: number | null;

    public cacheDuration: number;

    public constructor() {
        super();
        this.suggestsCache = null;
        this.cacheTimestamp = null;
        this.cacheDuration = 5 * 60 * 1000;
    }

    public async saveSearchHistory(
        userId: number,
        query: string,
        searchType: string,
        sectionId = "products",
    ): Promise<void> {
        try {
            const payload: SaveSearchHistoryPayload = {
                query,
                search_type: toSearchTypeValue(searchType),
                section_id: toSectionIdValue(sectionId),
            };
            await super.apiPost("/api/v1/search/history", { ...payload, user_id: userId });
        } catch (error) {
            console.error("Failed to save search history:", error);
        }
    }

    public async fetchSuggestions(): Promise<SearchSuggestItem[]> {
        if (this.suggestsCache && this.cacheTimestamp) {
            const now = Date.now();
            if (now - this.cacheTimestamp < this.cacheDuration) {
                return this.suggestsCache;
            }
        }

        try {
            const response = await super.apiGet("/api/v1/search/suggests", { noSpinner: true });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = (await response.json()) as SearchSuggestionsResponse | SearchSuggestItem[];
            const suggestions = Array.isArray(data)
                ? data
                : Array.isArray(data.data?.suggestions)
                  ? data.data.suggestions
                  : [];

            // The API's actual raw field is `search_type` (e.g. "brand") — the
            // OpenAPI-generated type for this response only declares `.type`,
            // which the real payload never sets (spec/implementation
            // mismatch: SearchController::getSuggests() dumps the mock file
            // as-is rather than the DTO shape `type` was generated from).
            // Normalize onto `.type` (what render/handler/setup all read)
            // here, once, instead of the reverse (which used to overwrite the
            // real search_type value with undefined, since item.type never
            // existed at runtime).
            this.suggestsCache = suggestions.map((item) => {
                const rawItem = item as SearchSuggestionsApiItem & { search_type?: string };
                return {
                    ...item,
                    type: (rawItem.search_type ?? item.type) as SearchSuggestionsApiItem["type"],
                };
            });
            this.cacheTimestamp = Date.now();

            return this.suggestsCache;
        } catch (error) {
            console.error("Failed to fetch search suggestions:", error);
            return [];
        }
    }

    /**
     * Live, full-catalog product search (see ISS-04-103) — unlike the rest of
     * search_type (brand/category/user/post_title), products are far too
     * numerous (~21k) to fit in the pre-generated snapshot that fetchSuggestions()
     * caches, so a fresh query goes to the server on every call instead.
     */
    private async searchProductsLive(query: string, limit: number): Promise<SearchSuggestItem[]> {
        try {
            const params = new URLSearchParams({ q: query, limit: String(limit) });
            const response = await super.apiGet(`/api/v1/search/suggests?${params.toString()}`, { noSpinner: true });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = (await response.json()) as SearchSuggestionsResponse | SearchSuggestItem[];
            const suggestions = Array.isArray(data)
                ? data
                : Array.isArray(data.data?.suggestions)
                  ? data.data.suggestions
                  : [];

            return suggestions
                .map((item) => {
                    const rawItem = item as SearchSuggestionsApiItem & { search_type?: string };
                    return { ...item, type: (rawItem.search_type ?? item.type) as SearchSuggestionsApiItem["type"] };
                })
                .filter((item) => item.type === "product");
        } catch (error) {
            console.error("Failed to fetch live product suggestions:", error);
            return [];
        }
    }

    public async searchSuggestions(
        query: string,
        options: SearchSuggestSearchOptions = {},
    ): Promise<SearchSuggestItem[]> {
        if (!query || query.trim().length === 0) {
            return [];
        }

        const normalizedQuery = query.toLowerCase().trim();
        const { searchType, sectionId, boardId, excludeTypes, limit = 10 } = options;
        // A caller that passes boardId wants this scoped to one board's own
        // content (e.g. /posts/{board_id}'s keyword search) — querying the
        // full product catalog live on every keystroke there just burns a
        // request for a near-guaranteed zero (article titles/phrases rarely
        // match a product_name), so skip it regardless of searchType.
        const includesProduct = (!searchType || searchType === "product") && !boardId;

        const [pool, liveProducts] = await Promise.all([
            this.fetchSuggestions(),
            includesProduct ? this.searchProductsLive(query.trim(), limit) : Promise.resolve([]),
        ]);

        // Live results supersede the snapshot's own (small, quota-limited)
        // product entries entirely, so the same product can't appear twice.
        const poolWithoutStaleProducts = includesProduct ? pool.filter((item) => item.type !== "product") : pool;

        let results = poolWithoutStaleProducts.filter((item) => {
            const text = String(item.text || "").toLowerCase();
            const value = String(item.value ?? "").toLowerCase();
            const normalizedText = text.replace(/^@/, "");
            const normalizedValue = value.replace(/^@/, "");
            const isUserSuggestion = item.type === "user";
            const textMatch = isUserSuggestion
                ? normalizedText.startsWith(normalizedQuery) || normalizedValue.startsWith(normalizedQuery)
                : text.includes(normalizedQuery) || value.includes(normalizedQuery);

            if (!textMatch) return false;
            if (searchType && item.type !== searchType) return false;
            if (excludeTypes && item.type && excludeTypes.includes(item.type)) return false;
            if (sectionId && item.metadata?.section_id !== sectionId) return false;
            // boardId only makes sense for (and only applies to) post_title —
            // brand/product/category/user don't carry a metadata.board_id at
            // all, so applying this check to them unconditionally would wipe
            // out every non-article suggestion whenever boardId is set.
            if (boardId && item.type === "post_title" && item.metadata?.board_id !== boardId) return false;

            return true;
        });

        results = [...results, ...liveProducts];

        // Ranking is keyword-match quality only (exact > starts-with >
        // contains) — item.score (popularity/rating-derived) deliberately
        // plays no part, by request.
        results = results.map((item) => {
            const text = item.text?.toLowerCase() || "";
            let matchScore = 0;

            if (text === normalizedQuery) {
                matchScore = 300;
            } else if (text.startsWith(normalizedQuery)) {
                matchScore = 200;
            } else if (text.includes(normalizedQuery)) {
                matchScore = 100;
            }

            return { ...item, match_score: matchScore, total_score: matchScore };
        });

        results.sort((a, b) => Number(b.total_score || 0) - Number(a.total_score || 0));

        return results.slice(0, limit);
    }

    public async getTrendingSuggestions(limit = 10): Promise<SearchSuggestItem[]> {
        const suggestions = await this.fetchSuggestions();

        return suggestions
            .sort((a, b) => Number(b.frequency_score || 0) - Number(a.frequency_score || 0))
            .slice(0, limit);
    }

    public clearCache(): void {
        this.suggestsCache = null;
        this.cacheTimestamp = null;
    }
}

export { SearchSuggestApi };

export const searchSuggestApi = new SearchSuggestApi();
